/**
 * DateSplitter — centred chip between message clusters (issue #351,
 * subtask 04). Shows "Today" / "Yesterday" / DD.MM.YYYY based on the
 * sent_at of the first message in the cluster.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

export interface DateSplitterProps {
  iso: string;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatLabel(iso: string, t: (key: string, fallback: string) => string): string {
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return '';
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(target, now)) return t('chat.dateSplitter.today', 'Today');
  if (sameDay(target, yesterday)) return t('chat.dateSplitter.yesterday', 'Yesterday');
  const dd = String(target.getDate()).padStart(2, '0');
  const mm = String(target.getMonth() + 1).padStart(2, '0');
  const yyyy = target.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

export const DateSplitter: React.FC<DateSplitterProps> = ({ iso }) => {
  const { t } = useTranslation();
  return (
    <div className="chat-date-splitter" role="separator" aria-label={formatLabel(iso, t)}>
      <span>{formatLabel(iso, t)}</span>
    </div>
  );
};

/**
 * Insert ``<DateSplitter>`` markers between an oldest→newest message
 * array whenever the date changes. Returns a flat React-renderable list.
 */
export function withDateSplitters<T extends { sent_at: string; message_id: string }>(
  messagesOldestFirst: T[],
  render: (msg: T) => React.ReactNode,
): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let lastDate: string | null = null;
  for (const msg of messagesOldestFirst) {
    const d = msg.sent_at.slice(0, 10);
    if (d !== lastDate) {
      out.push(<DateSplitter key={`split-${msg.message_id}`} iso={msg.sent_at} />);
      lastDate = d;
    }
    out.push(render(msg));
  }
  return out;
}
